import { loadingSpinnerGif, retryUntilLoad, vueAppStore } from '@amzn/AmazonJourneysVueApp';
import type {
  AssetUploadProgressedEvent,
  UserSelectedAssetsEvent,
} from '@amzn/AmazonJourneysVueApp/interop/components/asset/store/asset.events';
import { faEye, faImage, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type FC, type MouseEvent, useEffect, useState } from 'react';
import { filter } from 'rxjs/operators';
import { stylesheet } from 'typestyle';
import { grayBorder } from '../../../app.styles';
import { namespaceEventType } from '../../../app.util';
import { useVueAppState } from '../campaigns/campaigns.util';

export type SlateImageSelectorProps = {
  dataCy: string;
  assetId?: string | null;
  imageUrl?: string | null;
  slateId: string;
  onChange: (assetId: string) => void;
  id?: string;
  title?: string;
  ariaLabel?: string;
};

const hoverStyles = {
  $nest: {
    '& .action-icons': {
      visibility: 'visible',
    },
    '& .image-placeholder': {
      visibility: 'hidden',
    },
    '&:before': {
      background: 'black',
      borderRadius: 4,
      bottom: 0,
      content: "' '",
      display: 'block',
      left: 0,
      opacity: 0.4,
      position: 'absolute',
      right: 0,
      top: 0,
    },
  },
} as const;

const css = stylesheet({
  actionButton: {
    $nest: {
      '&:hover': {
        transform: 'scale(1.1)',
        transition: 'transform 0.2s',
      },
    },
    background: 'transparent',
    border: 'none',
    color: '#FFFFFF',
    cursor: 'pointer',
    fontSize: '1rem',
    padding: '0.25rem',
  },
  container: {
    $nest: {
      '.action-icons': {
        alignItems: 'center',
        bottom: 0,
        display: 'flex',
        gap: '0.5rem',
        justifyContent: 'center',
        left: 0,
        padding: '0 0.5rem',
        position: 'absolute',
        right: 0,
        top: 0,
        visibility: 'hidden',
        zIndex: 1,
      },
      '&:focus-within': hoverStyles,
      '&:hover': hoverStyles,
    },
    border: grayBorder,
    borderRadius: '4px',
    position: 'relative',
    width: '100%',
  },
  image: {
    borderRadius: '4px',
    display: 'block',
    height: 'auto',
    maxHeight: '100px',
    objectFit: 'contain',
    width: '100%',
  },
  loading: {
    alignItems: 'center',
    backgroundPosition: 'center',
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    display: 'flex',
    height: '65px',
    justifyContent: 'center',
  },
  noImg: {
    alignItems: 'center',
    backgroundColor: '#eeeeee',
    color: '#bbbbbb',
    display: 'flex',
    height: '65px',
    justifyContent: 'center',
  },
});

export const SlateImageSelector: FC<SlateImageSelectorProps> = ({
  dataCy,
  slateId,
  id,
  title = 'Upload slate image',
  ariaLabel = title,
  assetId,
  imageUrl: possibleImageUrl,
  onChange,
}) => {
  // handle viewing full size image
  const handleViewFullSize = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (imageUrl) {
      window.open(imageUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // track the s3 signed url in the vueApp asset module
  const s3SignedUrl = useVueAppState((state) => {
    const asset = state.asset.assets[assetId ?? ''];
    if (asset) {
      return asset.s3SignedUrlGet;
    }

    return state.asset.signedGetUrls[assetId ?? ''];
  });

  // retry until S3 has the image ready
  const [imageUrl, setImageUrl] = useState<string>();
  const [imageLoading, setImageLoading] = useState<boolean>(false);
  useEffect(() => {
    const tryLoadImage = s3SignedUrl ?? possibleImageUrl;
    if (tryLoadImage) {
      setImageLoading(true);
      void retryUntilLoad(tryLoadImage).then(() => {
        setImageUrl(tryLoadImage);
        setImageLoading(false);
      });
    }
  }, [
    possibleImageUrl,
    s3SignedUrl,
  ]);

  // show spinner if s3 not ready yet
  const imgThumbStyle = imageLoading
    ? {
        backgroundImage: `url(${loadingSpinnerGif})`,
      }
    : {
        backgroundImage: `url(${imageUrl})`,
      };

  // on replace image, open image selector modal and listen from the response from vueapp
  const replaceImage = () => {
    const requestId = `from-slate-${slateId}`;
    vueAppStore.publish({
      multi: false,
      requestId,
      type: namespaceEventType('asset', 'AssetSelectionNeeded'),
    });

    // listen for selection of existing assets
    const selectedSub = vueAppStore.event$
      // @ts-expect-error
      .pipe(filter((evt) => evt.type === namespaceEventType('asset', 'UserSelectedAssets') && evt.requestId === requestId))
      .subscribe((evt) => {
        selectedSub.unsubscribe();
        const event = evt as unknown as UserSelectedAssetsEvent;

        // if existing asset selected, we can get assetId right away. Otherwise if it's a new asset
        // we have to wait until it finishes uploading
        const selection = event.selections[0];
        const asset = 'asset' in selection && selection.asset;
        if (asset) {
          onChange(asset.assetId);
        }
      });

    // listen for upload completion of new assets
    const uploadedSub = vueAppStore.event$
      // @ts-expect-error
      .pipe(filter((evt) => evt.type === namespaceEventType('asset', 'AssetUploadProgressed') && evt.requestId === requestId))
      .subscribe((evt) => {
        const { asset, progress } = evt as unknown as AssetUploadProgressedEvent;
        if (progress.done) {
          uploadedSub.unsubscribe();
          onChange(asset.id);
        }
      });
  };

  return (
    <div className={css.container} data-cy={dataCy} data-value={assetId} id={id} tabIndex={0}>
      {imageLoading && <div className={css.loading} style={imgThumbStyle} />}
      {imageUrl && !imageLoading && <img alt={title} className={css.image} src={imageUrl} />}
      {!imageUrl && !imageLoading && (
        <div className={css.noImg}>
          <FontAwesomeIcon className="image-placeholder" icon={faImage} size="lg" />
        </div>
      )}
      <div className="action-icons">
        {imageUrl && (
          <button
            aria-label="View full size"
            className={css.actionButton}
            data-cy={`${dataCy}-view`}
            onClick={handleViewFullSize}
            title="View full size"
            type="button"
          >
            <FontAwesomeIcon icon={faEye} />
          </button>
        )}
        <button
          aria-label={ariaLabel}
          className={css.actionButton}
          data-cy={`${dataCy}-upload`}
          onClick={replaceImage}
          title={title}
          type="button"
        >
          <FontAwesomeIcon icon={faUpload} />
        </button>
      </div>
    </div>
  );
};
